/* eslint-disable */

import React, { useEffect, useState } from "react";
import {
  EmailAuthProvider,
  TotpMultiFactorGenerator,
  multiFactor,
  reauthenticateWithCredential,
} from "firebase/auth";

import { httpsCallable } from "firebase/functions";

import {
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

import { auth, db, functions } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import QRCode from "qrcode";

export default function MfaSetupPage() {
  const { role, profile } = useAuth() || {};

  const [step, setStep] = useState("start");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [secret, setSecret] = useState(null);
  const [qrUrl, setQrUrl] = useState("");
  const [code, setCode] = useState("");

  const [enablingTotp, setEnablingTotp] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setEmail(auth.currentUser?.email || "");
  }, []);

  const normalisedRole = String(role || profile?.role || "").toLowerCase();
  const isSuperAdmin =
    normalisedRole === "super_admin" || normalisedRole === "super-admin";

  async function enableTotpOnce() {
    try {
      setError("");
      setSuccess("");
      setEnablingTotp(true);

      const callable = httpsCallable(functions, "enableTotpMfa");
      const res = await callable({});

      setSuccess(res.data?.message || "TOTP MFA enabled. Refresh and try again.");
    } catch (err) {
      console.error(err);
      setError(err?.message || "Could not enable TOTP MFA.");
    } finally {
      setEnablingTotp(false);
    }
  }

  async function reauthenticate() {
    try {
      setError("");
      setSuccess("");

      const user = auth.currentUser;
      if (!user?.email) throw new Error("No signed-in user found.");

      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      const session = await multiFactor(user).getSession();
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);

      setSecret(totpSecret);
      const otpauthUrl = totpSecret.generateQrCodeUrl(user.email, "PresentPlus");
const qrDataUrl = await QRCode.toDataURL(otpauthUrl, {
  margin: 2,
  width: 240,
});

setQrUrl(qrDataUrl);
      setStep("scan");
    } catch (err) {
      console.error(err);
      setError(err?.message || "Could not start MFA setup.");
    }
  }

  async function enrolMfa() {
    try {
      setError("");
      setSuccess("");

      const user = auth.currentUser;
      if (!user) throw new Error("No signed-in user found.");
      if (!secret) throw new Error("MFA secret not generated.");

      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secret,
        code
      );

      await multiFactor(user).enroll(assertion, "PresentPlus Authenticator");

      await updateDoc(doc(db, "users", user.uid), {
        mfaCompleted: true,
        mfaCompletedAt: serverTimestamp(),
      });

      setSuccess("MFA has been enabled successfully.");
      setStep("complete");
    } catch (err) {
      console.error(err);
      setError(err?.message || "Could not verify MFA code.");
    }
  }

  function goAfterMfa() {
    if (isSuperAdmin) {
      window.location.href = "/super-admin";
      return;
    }

    window.location.href = "/admin-manager?tab=overview";
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.badge}>Admin security</div>

        <h1 style={styles.title}>Set up MFA</h1>

        <p style={styles.text}>
          Protect your PresentPlus admin account with an authenticator app such
          as Google Authenticator, Microsoft Authenticator, or 1Password.
        </p>

        {error ? <div style={styles.error}>{error}</div> : null}
        {success ? <div style={styles.success}>{success}</div> : null}

        {isSuperAdmin ? (
          <button
            type="button"
            style={{ ...styles.secondaryBtn, marginTop: 18 }}
            onClick={enableTotpOnce}
            disabled={enablingTotp}
          >
            {enablingTotp ? "Enabling TOTP..." : "Enable TOTP MFA Once"}
          </button>
        ) : null}

        {step === "start" ? (
          <div style={styles.panel}>
            <h2 style={styles.panelTitle}>Confirm your password</h2>
            <p style={styles.panelText}>
              For security, confirm your current password before enabling MFA.
            </p>

            <label style={styles.field}>
              <span>Email</span>
              <input style={styles.input} value={email} readOnly />
            </label>

            <label style={styles.field}>
              <span>Password</span>
              <input
                style={styles.input}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your current password"
              />
            </label>

            <button type="button" style={styles.primaryBtn} onClick={reauthenticate}>
              Continue
            </button>
          </div>
        ) : null}

        {step === "scan" ? (
          <div style={styles.panel}>
            <h2 style={styles.panelTitle}>Scan QR code</h2>
            <p style={styles.panelText}>
              Scan this QR code with your authenticator app, then enter the
              6-digit code.
            </p>

            {qrUrl ? (
              <div style={styles.qrBox}>
                <img src={qrUrl} alt="MFA QR code" style={styles.qr} />
              </div>
            ) : null}

            <label style={styles.field}>
              <span>Verification code</span>
              <input
                style={styles.input}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
              />
            </label>

            <button type="button" style={styles.primaryBtn} onClick={enrolMfa}>
              Enable MFA
            </button>
          </div>
        ) : null}

        {step === "complete" ? (
          <div style={styles.panel}>
            <h2 style={styles.panelTitle}>MFA protected</h2>
            <p style={styles.panelText}>
              Your admin account now has additional sign-in protection.
            </p>

            <button type="button" style={styles.primaryBtn} onClick={goAfterMfa}>
              Continue to Dashboard
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: 24,
    background:
      "radial-gradient(circle at top right, rgba(124,58,237,0.16), transparent 30%), linear-gradient(135deg,#eef5ff 0%,#f8fbff 55%,#f3e8ff 100%)",
  },
  card: {
    width: "100%",
    maxWidth: 760,
    borderRadius: 34,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    boxShadow: "0 28px 90px rgba(15,23,42,0.12)",
    padding: 40,
  },
  badge: {
    display: "inline-flex",
    borderRadius: 999,
    padding: "10px 14px",
    background: "#eef2ff",
    color: "#4338ca",
    fontWeight: 950,
    marginBottom: 18,
  },
  title: {
    margin: 0,
    fontSize: 48,
    lineHeight: 1,
    letterSpacing: "-0.06em",
    color: "#0f172a",
    fontWeight: 950,
  },
  text: {
    marginTop: 16,
    color: "#475569",
    lineHeight: 1.7,
    fontSize: 16,
  },
  panel: {
    marginTop: 28,
    borderRadius: 28,
    background: "#f8fbff",
    border: "1px solid #dbeafe",
    padding: 26,
    display: "grid",
    gap: 16,
  },
  panelTitle: {
    margin: 0,
    fontSize: 28,
    color: "#0f172a",
    fontWeight: 950,
    letterSpacing: "-0.05em",
  },
  panelText: {
    margin: 0,
    color: "#64748b",
    lineHeight: 1.7,
  },
  field: {
    display: "grid",
    gap: 8,
    fontWeight: 900,
    color: "#0f172a",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 18,
    border: "1px solid #cfe0ff",
    background: "#ffffff",
    padding: "15px 16px",
    fontSize: 15,
    color: "#0f172a",
    outline: "none",
  },
  primaryBtn: {
    border: "none",
    borderRadius: 18,
    padding: "16px 22px",
    background: "linear-gradient(135deg,#2563eb 0%,#7c3aed 100%)",
    color: "#ffffff",
    fontWeight: 950,
    cursor: "pointer",
  },
  secondaryBtn: {
    border: "1px solid #dbe7ff",
    borderRadius: 18,
    padding: "14px 18px",
    background: "#ffffff",
    color: "#334155",
    fontWeight: 950,
    cursor: "pointer",
  },
  qrBox: {
    display: "grid",
    placeItems: "center",
    borderRadius: 24,
    background: "#ffffff",
    border: "1px solid #dbeafe",
    padding: 20,
  },
  qr: {
    width: 220,
    height: 220,
  },
  error: {
    marginTop: 18,
    borderRadius: 18,
    padding: 14,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
    fontWeight: 850,
  },
  success: {
    marginTop: 18,
    borderRadius: 18,
    padding: 14,
    background: "#ecfdf3",
    border: "1px solid #bbf7d0",
    color: "#166534",
    fontWeight: 850,
  },
};